import React, { useEffect } from 'react';
import { RouteComponentProps } from '@reach/router';
import { makeStyles, useLoading, useUseCase } from '@smile-next/uikit';
import { LoginForm } from '@/components/LoginForm/LoginForm';
import { LoginFormBody } from '@/components/LoginForm/LoginFormBody';
import { LoginFormFooter } from '@/components/LoginForm/LoginFormFooter';
import { LoginFormHeader } from '@/components/LoginForm/LoginFormHeader';
import { SSOLoginPageState } from '@/containers/SSOLogin/Page/SSOLoginPageState';
import { SSOLoginForm } from '@/containers/SSOLogin/Page/components/SSOLoginForm';
import { useLocationSearch } from '@/hooks/useLocationSearch';
import { useRouter } from '@/hooks/useRouter';
import { CloudId } from '@/models/domain/CloudId';
import { LoginSSOUseCase } from '@/usecases/LoginSSOUseCase';
import { VerifyAuthenticatedUserUseCase } from '@/usecases/VerifyAuthenticatedUserUseCase';

const useStyles = makeStyles((theme) => ({
    root: {
        height: 'calc(100vh - 56px)',
        display: 'flex',
        flexDirection: 'column',
    },
    main: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        flex: '1 1 auto',
    },
    errorMessage: {
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.error.main,
    },
}));

export const SSOLoginPage: React.FC<RouteComponentProps> = (props) => {
    const { state, usecase } = useUseCase(new SSOLoginPageState());
    const { cloudId, origin, close } = useLocationSearch();
    const { movePageAfterSuccessfulLogin } = useRouter();
    const { startCoverLoading, startLockLoading, endLoadingByName } = useLoading();
    const classes = useStyles();

    useEffect(() => {
        startCoverLoading('SSOLoginVerifyAuthenticatedUser');
    }, [startCoverLoading]);

    useEffect(() => {
        if (state.isAuthenticated === undefined) {
            // 初回レンダー時は無視する
        } else if (state.isAuthenticated) {
            movePageAfterSuccessfulLogin();
        }
    }, [movePageAfterSuccessfulLogin, state.isAuthenticated]);

    useEffect(() => {
        // 現在の認証情報を検証する
        usecase(new VerifyAuthenticatedUserUseCase())
            .execute()
            .catch(() => {
                // エラー発生時の処理は別途行っているためここでは何も行わない
            })
            .finally(() => {
                endLoadingByName('SSOLoginVerifyAuthenticatedUser');
            });

        const { endLoading } = startCoverLoading();
        usecase(new LoginSSOUseCase())
            .execute({ cloudId: new CloudId({ value: cloudId || '' }), origin, close })
            .finally(() => {
                endLoading();
            });
    }, [close, cloudId, endLoadingByName, origin, startCoverLoading, usecase]);

    const onClickSSOButton = async (inputCloudId: CloudId) => {
        const { endLoading } = startLockLoading();
        await usecase(new LoginSSOUseCase()).execute({
            cloudId: inputCloudId,
            origin,
            close,
        });
        endLoading();
    };

    return (
        <div className={classes.root}>
            <div className={classes.main}>
                <LoginForm>
                    <LoginFormHeader />
                    <LoginFormBody>
                        <SSOLoginForm cloudId={state.authParams?.cloudId} onClickSSOButton={onClickSSOButton} />
                        {state.errorMessage && <div className={classes.errorMessage}>{state.errorMessage}</div>}
                    </LoginFormBody>
                    <LoginFormFooter />
                </LoginForm>
            </div>
        </div>
    );
};
